"use client";

import { c as _c } from "react/compiler-runtime";
import MemesWaveQuickVoteTrigger from "@/components/brain/left-sidebar/waves/MemesWaveQuickVoteTrigger";
import MemesWaveZapIcon from "@/components/brain/left-sidebar/waves/MemesWaveZapIcon";
import { useMemesWaveFooterStats } from "@/hooks/useMemesWaveFooterStats";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { formatMemesQuickVoteLeftThisRoundText, formatMemesQuickVoteUnratedText } from "@/hooks/memesQuickVote.helpers";
import { AnimatePresence, motion } from "framer-motion";
import React from "react";
interface MemesWaveFooterProps {
  readonly collapsed?: boolean | undefined;
  readonly onOpenQuickVote: () => void;
  readonly onPrefetchQuickVote?: (() => void) | undefined;
}
const revealTransition = {
  duration: 0.22,
  ease: "easeOut"
} as const;
const MemesWaveFooter: React.FC<MemesWaveFooterProps> = t0 => {
  const $ = _c(29);
  const {
    collapsed: t1,
    onOpenQuickVote,
    onPrefetchQuickVote
  } = t0;
  const collapsed = t1 === undefined ? false : t1;
  const {
    isAvailable,
    isReady,
    leftThisRoundCount,
    uncastPower,
    unratedCount,
    votingLabel
  } = useMemesWaveFooterStats();
  let t2;
  if ($[0] !== isReady || $[1] !== leftThisRoundCount || $[2] !== uncastPower || $[3] !== unratedCount || $[4] !== votingLabel) {
    t2 = isReady && typeof uncastPower === "number" ? `Uncast Power, ${formatNumberWithCommas(uncastPower)} ${votingLabel ?? "Votes"} left, ${formatMemesQuickVoteLeftThisRoundText(leftThisRoundCount)}, ${formatMemesQuickVoteUnratedText(unratedCount)}` : "Quick vote";
    $[0] = isReady;
    $[1] = leftThisRoundCount;
    $[2] = uncastPower;
    $[3] = unratedCount;
    $[4] = votingLabel;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const buttonAriaLabel = t2;
  const buttonTitle = isReady ? "Uncast votes" : "Quick vote";
  const votingPowerLabel = votingLabel ? ` ${votingLabel}` : " votes";
  let t3;
  if ($[6] !== isReady || $[7] !== uncastPower || $[8] !== votingPowerLabel) {
    t3 = isReady && typeof uncastPower === "number" ? `${formatNumberWithCommas(uncastPower)}${votingPowerLabel}` : "Open quick vote";
    $[6] = isReady;
    $[7] = uncastPower;
    $[8] = votingPowerLabel;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const buttonValue = t3;
  let t4;
  if ($[10] !== isAvailable || $[11] !== onOpenQuickVote) {
    t4 = () => {
      if (!isAvailable) {
        return;
      }
      onOpenQuickVote();
    };
    $[10] = isAvailable;
    $[11] = onOpenQuickVote;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const handleOpenQuickVote = t4;
  let t5;
  if ($[13] !== isAvailable || $[14] !== onPrefetchQuickVote) {
    t5 = () => {
      if (!isAvailable) {
        return;
      }
      onPrefetchQuickVote?.();
    };
    $[13] = isAvailable;
    $[14] = onPrefetchQuickVote;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const handlePrefetchQuickVote = t5;
  let t6;
  if ($[16] !== buttonAriaLabel || $[17] !== buttonTitle || $[18] !== buttonValue || $[19] !== collapsed || $[20] !== handleOpenQuickVote || $[21] !== handlePrefetchQuickVote || $[22] !== isAvailable || $[23] !== isReady || $[24] !== leftThisRoundCount || $[25] !== unratedCount) {
    t6 = isAvailable && <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 16
    }} transition={revealTransition} className={collapsed ? "tw-z-10 tw-flex tw-flex-shrink-0 tw-justify-center tw-px-2 tw-pb-2 tw-pt-1" : "tw-relative tw-z-20 tw-mt-auto tw-flex-shrink-0"}>{collapsed ? <MemesWaveQuickVoteTrigger isAvailable={isAvailable} leftThisRoundCount={leftThisRoundCount} onOpenQuickVote={handleOpenQuickVote} onPrefetchQuickVote={handlePrefetchQuickVote} unratedCount={unratedCount} /> : <button type="button" aria-label={buttonAriaLabel} onClick={handleOpenQuickVote} onFocus={handlePrefetchQuickVote} onMouseEnter={handlePrefetchQuickVote} className="tw-group tw-mt-auto tw-w-full tw-flex-shrink-0 tw-cursor-pointer tw-border-0 tw-border-t tw-border-solid tw-border-iron-800/60 tw-bg-black tw-p-4 tw-text-left tw-transition-colors desktop-hover:hover:tw-bg-iron-900/40"><div className="tw-relative tw-flex tw-items-center tw-justify-between tw-gap-4 tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-[#2d3753] tw-bg-[#0c1018] tw-px-4 tw-py-2.5 tw-shadow-lg tw-transition-all tw-duration-200 desktop-hover:group-hover:tw-border-[#3a4670] desktop-hover:group-hover:tw-bg-[#0f1420]"><span aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-inset-0 -tw-translate-x-full tw-bg-gradient-to-r tw-from-white/0 tw-via-white/[0.08] tw-to-white/0 tw-opacity-50 tw-transition-transform tw-duration-1000 tw-ease-out desktop-hover:group-hover:tw-translate-x-full" /><div className="tw-relative tw-z-10 tw-flex tw-min-w-0 tw-flex-col tw-gap-1.5"><span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-[#6b7c93]">{buttonTitle}</span><div className="tw-flex tw-items-center tw-gap-2"><MemesWaveZapIcon className="tw-size-4 tw-flex-shrink-0 tw-fill-primary-400/20 tw-text-primary-400" /><span className="tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white">{buttonValue}</span></div></div>{isReady && <div className="tw-relative tw-z-10 tw-flex tw-flex-col tw-items-end tw-gap-0.5 tw-text-right"><span className="tw-text-xs tw-font-semibold tw-text-[#8199ea] tw-shadow-sm">{formatMemesQuickVoteLeftThisRoundText(leftThisRoundCount)}</span><span className="tw-text-[11px] tw-font-medium tw-text-[#6b7c93]">{formatMemesQuickVoteUnratedText(unratedCount)}</span></div>}</div></button>}</motion.div>;
    $[16] = buttonAriaLabel;
    $[17] = buttonTitle;
    $[18] = buttonValue;
    $[19] = collapsed;
    $[20] = handleOpenQuickVote;
    $[21] = handlePrefetchQuickVote;
    $[22] = isAvailable;
    $[23] = isReady;
    $[24] = leftThisRoundCount;
    $[25] = unratedCount;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== t6) {
    t7 = <AnimatePresence>{t6}</AnimatePresence>;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  return t7;
};
export default MemesWaveFooter;